import { useId, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Mail, Plus, Trash2, AlertCircle, CheckCircle2, Send } from "lucide-react";
import { ApiError, api } from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { LoadingState } from "@/components/data/LoadingState";
import { ErrorState } from "@/components/data/ErrorState";
import { EmptyState } from "@/components/data/EmptyState";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface NotificationTemplate {
  id: number;
  key: string;
  channel: "push" | "email";
  family: string;
  variant: string | null;
  label: string;
  trigger_summary: string;
  description: string | null;
  variables: string; // JSON-encoded array of names
  title: string;
  body: string;
  body_html: string | null;
  is_active: number;
  is_custom: number;
  created_at: string;
  updated_at: string;
  updated_by: string | null;
}

interface ListResponse {
  items: NotificationTemplate[];
}

// PATCH body; mirrors updateBody in apps/api/src/routes/admin.ts (is_active is a boolean there).
type TemplateUpdate = {
  title?: string;
  body?: string;
  body_html?: string | null;
  is_active?: boolean;
};

// Send counts per family (notification_log.notification_type), from one request.
interface FamilyStats {
  total: number;
  last7d: number;
  last30d: number;
  last_sent_at: string | null;
}

interface StatsResponse {
  items: Record<string, FamilyStats>;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function NotificationsPage() {
  const [editing, setEditing] = useState<NotificationTemplate | null>(null);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState<"all" | "push" | "email">("all");

  const list = useQuery<ListResponse>({
    queryKey: ["notification-templates"],
    queryFn: () => api.get("/api/admin/notification-templates"),
  });

  const stats = useQuery<StatsResponse>({
    queryKey: ["notification-template-stats"],
    queryFn: () => api.get("/api/admin/notification-templates/stats"),
    staleTime: 60_000,
  });

  const grouped = useMemo(() => groupByFamily(list.data?.items ?? [], filter), [list.data, filter]);

  const totalPush = (list.data?.items ?? []).filter((t) => t.channel === "push").length;
  const totalEmail = (list.data?.items ?? []).filter((t) => t.channel === "email").length;
  const totalInactive = (list.data?.items ?? []).filter((t) => t.is_active === 0).length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notifications"
        description="Every push and email the app sends, with the conditions that trigger them. Click any card to edit."
        actions={
          <Button onClick={() => setCreating(true)} size="sm">
            <Plus className="mr-1.5 h-4 w-4" /> New custom template
          </Button>
        }
      />

      <div className="flex items-center gap-2" role="group" aria-label="Filter templates by channel">
        <Pill active={filter === "all"} onClick={() => setFilter("all")}>
          All <span className="ml-1 text-muted-foreground">{totalPush + totalEmail}</span>
        </Pill>
        <Pill active={filter === "push"} onClick={() => setFilter("push")}>
          <Bell className="mr-1.5 h-3 w-3" aria-hidden="true" /> Push <span className="ml-1 text-muted-foreground">{totalPush}</span>
        </Pill>
        <Pill active={filter === "email"} onClick={() => setFilter("email")}>
          <Mail className="mr-1.5 h-3 w-3" aria-hidden="true" /> Email <span className="ml-1 text-muted-foreground">{totalEmail}</span>
        </Pill>
        {totalInactive > 0 && (
          <span className="ml-auto text-xs text-muted-foreground">
            {totalInactive} disabled
          </span>
        )}
      </div>

      {list.isPending && <LoadingState label="Loading templates…" />}
      {list.isError && <ErrorState error={list.error as Error} />}

      {list.data && grouped.length === 0 && (
        <EmptyState title="No templates" description="Try a different filter." />
      )}

      <div className="space-y-6">
        {grouped.map((g) => (
          <FamilyGroup
            key={`${g.channel}.${g.family}`}
            channel={g.channel}
            family={g.family}
            templates={g.templates}
            stats={g.channel === "push" ? stats.data?.items[g.family] : undefined}
            onEdit={setEditing}
          />
        ))}
      </div>

      {editing && (
        <EditDrawer
          template={editing}
          onClose={() => setEditing(null)}
        />
      )}

      {creating && (
        <CreateDialog onClose={() => setCreating(false)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Family group
// ---------------------------------------------------------------------------

interface GroupedFamily {
  channel: "push" | "email";
  family: string;
  templates: NotificationTemplate[];
}

function groupByFamily(items: NotificationTemplate[], filter: "all" | "push" | "email"): GroupedFamily[] {
  const out: Record<string, GroupedFamily> = {};
  for (const t of items) {
    if (filter !== "all" && t.channel !== filter) continue;
    const key = `${t.channel}.${t.family}`;
    if (!out[key]) out[key] = { channel: t.channel, family: t.family, templates: [] };
    out[key].templates.push(t);
  }
  // Channel order: push first, then email
  return Object.values(out).sort((a, b) => {
    if (a.channel !== b.channel) return a.channel === "push" ? -1 : 1;
    return a.family.localeCompare(b.family);
  });
}

function FamilyGroup({
  channel,
  family,
  templates,
  stats,
  onEdit,
}: {
  channel: "push" | "email";
  family: string;
  templates: NotificationTemplate[];
  stats?: FamilyStats;
  onEdit: (t: NotificationTemplate) => void;
}) {
  const Icon = channel === "push" ? Bell : Mail;
  return (
    <section>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          {prettyFamily(family)}
        </h2>
        <span className="text-xs text-muted-foreground">{templates.length}</span>
        {stats && <FamilyStatsLine stats={stats} />}
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {templates.map((t) => (
          <TemplateCard key={t.id} template={t} onEdit={onEdit} />
        ))}
      </div>
    </section>
  );
}

function prettyFamily(family: string): string {
  return family.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

function TemplateCard({
  template,
  onEdit,
}: {
  template: NotificationTemplate;
  onEdit: (t: NotificationTemplate) => void;
}) {
  const variables = parseVars(template.variables);
  return (
    <Card className="relative p-4 transition-colors focus-within:ring-2 focus-within:ring-ring hover:border-foreground/40">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-sm font-semibold">
              {/* Stretched button: the whole card is clickable, and keyboard users get one focusable control. */}
              <button
                type="button"
                onClick={() => onEdit(template)}
                className="text-left after:absolute after:inset-0 after:content-[''] focus-visible:outline-none"
              >
                {template.label}
              </button>
            </h3>
            {template.is_custom === 1 && (
              <Badge variant="outline" className="text-[10px]">custom</Badge>
            )}
            {template.is_active === 0 && (
              <Badge variant="outline" className="border-amber-500/50 text-[10px] text-amber-400">disabled</Badge>
            )}
          </div>
          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
            {template.trigger_summary}
          </p>
        </div>
      </div>

      <div className="mt-3 space-y-1.5 text-xs">
        {template.title && (
          <div className="text-foreground/90">
            <span className="text-muted-foreground">Title:</span> <span className="font-medium">{template.title}</span>
          </div>
        )}
        {template.body && (
          <div className="line-clamp-2 text-foreground/70">
            <span className="text-muted-foreground">Body:</span> {template.body}
          </div>
        )}
      </div>

      {variables.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {variables.map((v) => (
            <code key={v} className="rounded bg-secondary px-1.5 py-0.5 text-[10px] text-foreground/80">
              %{v}%
            </code>
          ))}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between text-[10px] text-muted-foreground">
        <code className="font-mono">{template.key}</code>
        <span>edited {fmtRel(template.updated_at)}</span>
      </div>
    </Card>
  );
}

// notification_log records the family, not the template variant, so counts
// are shown once per family rather than repeated on every card.
function FamilyStatsLine({ stats }: { stats: FamilyStats }) {
  if (stats.total === 0) {
    return <span className="text-xs text-muted-foreground">· never sent</span>;
  }
  return (
    <span className="text-xs text-muted-foreground">
      · <span className="font-semibold text-foreground/90">{stats.total.toLocaleString()}</span> sent ·{" "}
      <span className="font-semibold text-foreground/90">{stats.last7d.toLocaleString()}</span> this week
      {stats.last_sent_at && <> · last {fmtRel(stats.last_sent_at)}</>}
    </span>
  );
}

function parseVars(json: string): string[] {
  try {
    const v = JSON.parse(json);
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function fmtRel(iso: string): string {
  try {
    const dt = new Date(iso.replace(" ", "T") + "Z");
    const diff = Date.now() - dt.getTime();
    const min = Math.round(diff / 60_000);
    if (min < 1) return "just now";
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const d = Math.round(hr / 24);
    if (d < 30) return `${d}d ago`;
    return dt.toISOString().slice(0, 10);
  } catch {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// Edit drawer
// ---------------------------------------------------------------------------

function EditDrawer({
  template,
  onClose,
}: {
  template: NotificationTemplate;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const variables = parseVars(template.variables);

  const [title, setTitle] = useState(template.title);
  const [body, setBody] = useState(template.body);
  const [bodyHtml, setBodyHtml] = useState(template.body_html ?? "");
  const [isActive, setIsActive] = useState(template.is_active === 1);
  const [showHtml, setShowHtml] = useState(false);

  const dirty =
    title !== template.title ||
    body !== template.body ||
    (bodyHtml ?? "") !== (template.body_html ?? "") ||
    isActive !== (template.is_active === 1);

  // Sample variable values for the live preview — populated from variable
  // names with sensible mock data. Editable so the content team can sanity-
  // check edge cases.
  const [sampleVars, setSampleVars] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const v of variables) out[v] = sampleFor(v);
    return out;
  });

  const update = useMutation({
    mutationFn: (changes: TemplateUpdate) =>
      api.patch<NotificationTemplate>(`/api/admin/notification-templates/${template.id}`, changes),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notification-templates"] });
      onClose();
    },
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/api/admin/notification-templates/${template.id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notification-templates"] });
      onClose();
    },
  });

  const [testEmailOverride, setTestEmailOverride] = useState("");
  const sendTest = useMutation({
    mutationFn: () =>
      api.post<{ ok: true; channel: "push" | "email"; devices?: number; to?: string }>(
        `/api/admin/notification-templates/${template.id}/send-test`,
        {
          vars: sampleVars,
          to_email: testEmailOverride || undefined,
        },
      ),
  });

  function handleSave() {
    update.mutate({
      title,
      body,
      body_html: bodyHtml ? bodyHtml : null,
      is_active: isActive,
    });
  }

  const previewTitle = renderClient(title, sampleVars);
  const previewBody = renderClient(body, sampleVars);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {template.label}
            {template.is_custom === 1 && <Badge variant="outline" className="text-[10px]">custom</Badge>}
          </DialogTitle>
          <DialogDescription>
            <code className="rounded bg-secondary px-1 py-0.5 font-mono text-[11px]">{template.key}</code>
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          {/* Trigger summary */}
          <div className="rounded-md border border-border/60 bg-card/40 p-3 text-xs">
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <AlertCircle className="h-3.5 w-3.5" />
              <span className="font-semibold uppercase tracking-widest">Triggers when</span>
            </div>
            <p className="mt-1 text-foreground/90">{template.trigger_summary}</p>
            {template.description && (
              <p className="mt-2 text-muted-foreground">{template.description}</p>
            )}
          </div>

          {/* Variables */}
          {variables.length > 0 && (
            <div>
              <Label className="mb-2 inline-block text-xs">Available variables</Label>
              <div className="flex flex-wrap gap-1.5">
                {variables.map((v) => (
                  <code key={v} className="rounded bg-secondary px-1.5 py-0.5 text-[11px] text-foreground/80">
                    %{v}%
                  </code>
                ))}
              </div>
              <p className="mt-1.5 text-[11px] text-muted-foreground">
                Wrap a variable in <code>%name%</code> to substitute. Empty values are stripped along with surrounding punctuation.
              </p>
            </div>
          )}

          {/* Title */}
          {template.channel === "email" ? (
            <div>
              <Label htmlFor="t-title">Subject</Label>
              <Input id="t-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" />
            </div>
          ) : (
            <div>
              <Label htmlFor="t-title">Title {!template.title && <span className="text-muted-foreground">(supplied by another template — leave empty)</span>}</Label>
              <Input id="t-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" />
            </div>
          )}

          {/* Body */}
          <div>
            <Label htmlFor="t-body">Body{template.channel === "email" ? " (plaintext)" : ""}</Label>
            <Textarea
              id="t-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={template.channel === "email" ? 10 : 4}
              className="mt-1 font-mono text-xs"
            />
          </div>

          {/* Body HTML for emails */}
          {template.channel === "email" && (
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="t-body-html">HTML body</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowHtml((s) => !s)}
                >
                  {showHtml ? "Hide" : "Edit HTML"}
                </Button>
              </div>
              {showHtml && (
                <Textarea
                  id="t-body-html"
                  value={bodyHtml}
                  onChange={(e) => setBodyHtml(e.target.value)}
                  rows={20}
                  className="mt-1 font-mono text-[11px]"
                  placeholder="(empty — only plaintext will be sent)"
                />
              )}
            </div>
          )}

          {/* Sample vars + Preview */}
          {variables.length > 0 && (
            <div>
              <Label className="mb-2 inline-block text-xs">Preview values</Label>
              <div className="grid gap-2 md:grid-cols-2">
                {variables.map((v) => (
                  <div key={v} className="flex items-center gap-2">
                    <code className="shrink-0 rounded bg-secondary px-1.5 py-0.5 text-[11px]">%{v}%</code>
                    <Input
                      aria-label={`Preview value for %${v}%`}
                      value={sampleVars[v] ?? ""}
                      onChange={(e) => setSampleVars((s) => ({ ...s, [v]: e.target.value }))}
                      className="h-8 text-xs"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-md border border-border/60 bg-background/50 p-3">
            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
              Live preview
            </div>
            {previewTitle && (
              <div className="text-sm font-semibold">{previewTitle}</div>
            )}
            {previewBody && (
              <div className="mt-1 whitespace-pre-wrap text-sm text-foreground/85">{previewBody}</div>
            )}
            {!previewTitle && !previewBody && (
              <div className="text-xs text-muted-foreground">(empty)</div>
            )}
          </div>

          {/* Active toggle */}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4"
            />
            <span>Active</span>
            <span className="text-xs text-muted-foreground">
              {isActive ? "— will be sent when triggered" : "— send sites will skip this template"}
            </span>
          </label>

          {/* Send-test panel — fires the rendered template at the admin's
              own device (push) or email address. Lets the content team
              eyeball edits on a real device before saving. */}
          <div className="rounded-md border border-border/60 bg-card/30 p-3">
            <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
              <Send className="h-3 w-3" /> Send test
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {template.channel === "push"
                ? "Sends to every iPhone registered against your admin account, using the preview values above."
                : "Sends to the email below (defaults to your admin email)."}
            </p>
            {template.channel === "email" && (
              <Input
                value={testEmailOverride}
                onChange={(e) => setTestEmailOverride(e.target.value)}
                placeholder="you@example.com"
                className="mt-2 h-8 text-xs"
                type="email"
              />
            )}
            <div className="mt-2 flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => sendTest.mutate()}
                disabled={sendTest.isPending}
              >
                <Send className="mr-1.5 h-3 w-3" />
                {sendTest.isPending ? "Sending…" : "Send test now"}
              </Button>
              {sendTest.isSuccess && (
                <span className="flex items-center gap-1 text-xs text-emerald-400">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {sendTest.data?.channel === "push"
                    ? `Sent to ${sendTest.data.devices} device${sendTest.data.devices === 1 ? "" : "s"}`
                    : `Sent to ${sendTest.data?.to}`}
                </span>
              )}
              {sendTest.isError && (
                <span className="text-xs text-rose-400">{(sendTest.error as Error).message}</span>
              )}
            </div>
          </div>

          {update.isError && (
            <div className="rounded-md bg-rose-500/10 px-3 py-2 text-xs text-rose-400">
              Save failed: {saveErrorMessage(update.error)}
            </div>
          )}
          {update.isSuccess && !dirty && (
            <div className="flex items-center gap-1.5 rounded-md bg-emerald-500/10 px-3 py-2 text-xs text-emerald-400">
              <CheckCircle2 className="h-3.5 w-3.5" /> Saved
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between sm:justify-between">
          <div>
            {template.is_custom === 1 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-rose-400 hover:bg-rose-500/10"
                onClick={() => {
                  if (confirm(`Delete custom template "${template.label}"? This cannot be undone.`)) {
                    remove.mutate();
                  }
                }}
                disabled={remove.isPending}
              >
                <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={update.isPending}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!dirty || update.isPending}>
              {update.isPending ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Names the first invalid field when the API rejects the body with zod issues.
function saveErrorMessage(err: unknown): string {
  const message = err instanceof Error ? err.message : "Unknown error";
  if (err instanceof ApiError && err.body && typeof err.body === "object" && "issues" in err.body) {
    const issues = (err.body as { issues?: Array<{ path?: unknown[]; message?: string }> }).issues;
    const first = issues?.[0];
    if (first) return `${message}: ${(first.path ?? []).join(".") || "body"} ${first.message ?? ""}`.trim();
  }
  return message;
}

// Client-side variable substitution that mirrors the server's render() — used
// only for the live preview, never for sending.
function renderClient(template: string, vars: Record<string, string>): string {
  let out = template;
  for (const [key, raw] of Object.entries(vars)) {
    if (!raw) continue;
    out = out.replace(new RegExp(escapeRegExp(`%${key}%`), "g"), raw);
  }
  for (const key of Object.keys(vars)) {
    const placeholder = escapeRegExp(`%${key}%`);
    out = out
      .replace(new RegExp(`${placeholder}, `, "g"), "")
      .replace(new RegExp(`${placeholder} `, "g"), "")
      .replace(new RegExp(`, ${placeholder}`, "g"), "")
      .replace(new RegExp(placeholder, "g"), "");
  }
  return out.replace(/  +/g, " ").replace(/^\s+/, "");
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function sampleFor(name: string): string {
  // Heuristics to pre-fill plausible preview values. Content team can edit.
  const lower = name.toLowerCase();
  if (lower === "name" || lower === "greeting") return "Sam";
  if (lower === "code") return "847295";
  if (lower === "verifyurl") return "https://billypocket.com/verify/abc123";
  if (lower === "position") return "127";
  if (lower === "monthname") return "March";
  if (lower === "totalstr") return "$1,240";
  if (lower === "spentstr") return "$840";
  if (lower === "remainingstr") return "$160";
  if (lower === "dailyavg") return "$42";
  if (lower === "label") return "your monthly budget";
  if (lower === "pct") return "85";
  if (lower === "count") return "4";
  if (lower === "txword") return "logs";
  if (lower === "bill") return "Netflix ($12)";
  if (lower === "bills") return "Netflix ($12), Rent ($1,400)";
  if (lower === "head") return "Netflix, Rent";
  if (lower === "more") return "3";
  if (lower === "totalline") return "$1,452";
  if (lower === "streak") return "7";
  return "";
}

// ---------------------------------------------------------------------------
// Create dialog
// ---------------------------------------------------------------------------

function CreateDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [key, setKey] = useState("");
  const [channel, setChannel] = useState<"push" | "email">("push");
  const [family, setFamily] = useState("custom");
  const [variant, setVariant] = useState("");
  const [label, setLabel] = useState("");
  const [triggerSummary, setTriggerSummary] = useState("");
  const [variables, setVariables] = useState("name");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const uid = useId();

  const create = useMutation({
    mutationFn: () => api.post("/api/admin/notification-templates", {
      key: key.trim(),
      channel,
      family: family.trim(),
      variant: variant.trim() || null,
      label: label.trim(),
      trigger_summary: triggerSummary.trim(),
      variables: variables.split(",").map((v) => v.trim()).filter(Boolean),
      title,
      body,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notification-templates"] });
      onClose();
    },
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New custom template</DialogTitle>
          <DialogDescription>
            Adds an editable copy entry. Custom templates aren't tied to a code-side trigger — useful for ad-hoc broadcasts you'll send manually, or to draft copy for an upcoming feature.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label htmlFor={`${uid}-channel`}>Channel</Label>
              <select
                id={`${uid}-channel`}
                value={channel}
                onChange={(e) => setChannel(e.target.value as "push" | "email")}
                className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="push">Push</option>
                <option value="email">Email</option>
              </select>
            </div>
            <div>
              <Label htmlFor={`${uid}-key`}>Key</Label>
              <Input id={`${uid}-key`} value={key} onChange={(e) => setKey(e.target.value)} placeholder="push.custom.my_template" className="mt-1 font-mono text-xs" />
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label htmlFor={`${uid}-family`}>Family</Label>
              <Input id={`${uid}-family`} value={family} onChange={(e) => setFamily(e.target.value)} placeholder="custom" className="mt-1" />
            </div>
            <div>
              <Label htmlFor={`${uid}-variant`}>Variant (optional)</Label>
              <Input id={`${uid}-variant`} value={variant} onChange={(e) => setVariant(e.target.value)} className="mt-1" />
            </div>
          </div>
          <div>
            <Label htmlFor={`${uid}-label`}>Label</Label>
            <Input id={`${uid}-label`} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Friendly name for this template" className="mt-1" />
          </div>
          <div>
            <Label htmlFor={`${uid}-trigger`}>Trigger summary</Label>
            <Input
              id={`${uid}-trigger`}
              value={triggerSummary}
              onChange={(e) => setTriggerSummary(e.target.value)}
              placeholder="When does this fire? (free text)"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor={`${uid}-vars`}>Variables (comma-separated)</Label>
            <Input id={`${uid}-vars`} value={variables} onChange={(e) => setVariables(e.target.value)} placeholder="name,amount" className="mt-1 font-mono text-xs" />
          </div>
          <div>
            <Label htmlFor={`${uid}-title`}>{channel === "email" ? "Subject" : "Title"}</Label>
            <Input id={`${uid}-title`} value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label htmlFor={`${uid}-body`}>Body</Label>
            <Textarea id={`${uid}-body`} value={body} onChange={(e) => setBody(e.target.value)} rows={6} className="mt-1 font-mono text-xs" />
          </div>

          {create.isError && (
            <div className="rounded-md bg-rose-500/10 px-3 py-2 text-xs text-rose-400">
              {(create.error as Error).message}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => create.mutate()}
            disabled={!key || !label || !triggerSummary || create.isPending}
          >
            {create.isPending ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Filter pill
// ---------------------------------------------------------------------------

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs transition-colors ${
        active
          ? "border-foreground bg-foreground text-background"
          : "border-border/60 bg-card/40 text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}
